const LIVE_OFFER_STATUSES = new Set(["live", "approved"]);

const APPROVAL_SAFE_EDIT_FIELDS = new Set([
  "title",
  "description",
  "category",
  "subCategory",
  "offerDiscountType",
  "redemptionFrequency",
]);

const LIVE_OFFER_COMPARISON_FIELDS = new Set([
  "title",
  "description",
  "category",
  "subCategory",
  "offerType",
  "offerDiscountType",
  "onlineOption",
  "discountRange",
  "fixedDollarAmount",
  "offerCodes",
  "websiteURL",
  "thumbnailImage",
  "previewImages",
  "ruleAndRegulation",
  "locations",
  "availableAcrossAllStores",
  "redemptionFrequency",
  "redemptionDays",
  "redemptionMethod",
  "startTime",
  "endTime",
  "callback_url",
  "document",
  "discountTitle",
  "necessaryRule",
  "startDate",
  "endDate",
]);

const normalizeOfferType = (offerType) => {
  const normalized = String(offerType || "").toUpperCase();
  if (normalized === "INSTORE_OFFER" || normalized === "INSTORE_SWOODLE_OFFER") {
    return "INSTORE";
  }
  if (normalized === "ONLINE_OFFER") {
    return "ONLINE";
  }
  return normalized;
};

const getComparisonContext = (currentPayload, originalOffer) => {
  const offerType = normalizeOfferType(currentPayload?.offerType || originalOffer?.offerType);
  const discountType = String(currentPayload?.offerDiscountType || originalOffer?.offerDiscountType || "").toUpperCase();

  return { offerType, discountType };
};

const shouldIgnoreField = (field, currentPayload, originalOffer) => {
  const { offerType, discountType } = getComparisonContext(currentPayload, originalOffer);

  if (field === "discountRange" && discountType !== "DISCOUNTED_PERCENT") {
    return true;
  }

  if (field === "fixedDollarAmount" && discountType !== "FIXED_DOLLAR") {
    return true;
  }

  if ((field === "onlineOption" || field === "websiteURL" || field === "offerCodes") && offerType !== "ONLINE_OFFER") {
    return true;
  }

  if ((field === "locations" || field === "availableAcrossAllStores") && !offerType.startsWith("INSTORE")) {
    return true;
  }

  return false;
};

const getCanonicalLiveOfferPayload = (payload) => {
  const canonical = { ...(payload || {}) };
  const offerType = normalizeOfferType(canonical?.offerType);
  const discountType = String(canonical?.offerDiscountType || "").toUpperCase();

  if (offerType) {
    canonical.offerType = offerType;
  }

  if (!canonical.locations && Array.isArray(canonical.tempLocations)) {
    canonical.locations = canonical.tempLocations;
  }

  if (!canonical.ruleAndRegulation && Array.isArray(canonical.rules)) {
    canonical.ruleAndRegulation = canonical.rules;
  }

  if (discountType !== "DISCOUNTED_PERCENT") {
    delete canonical.discountRange;
  }

  if (discountType !== "FIXED_DOLLAR") {
    delete canonical.fixedDollarAmount;
  }

  if (offerType !== "ONLINE_OFFER") {
    delete canonical.onlineOption;
    delete canonical.websiteURL;
    delete canonical.offerCodes;
  }

  delete canonical.tempLocations;
  delete canonical.rules;

  return canonical;
};

const normalizeOfferValue = (field, value) => {
  if (value == null) return "";

  if (field === "description" || field === "title" || field === "websiteURL" || field === "callback_url" || field === "discountTitle" || field === "necessaryRule" || field === "redemptionMethod") {
    return String(value).trim();
  }

  if (field === "category" && typeof value === "object") {
    return value?._id || value?.id || "";
  }

  if (field === "availableAcrossAllStores") {
    return value === true || value === "true" ? "true" : "";
  }

  if (field === "fixedDollarAmount" || field === "discountRange") {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue === 0) return "";
    return String(numericValue);
  }

  if (field === "startDate" || field === "endDate") {
    const str = String(value).trim();
    if (!str) return "";
    return str.slice(0, 10);
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item == null) return "";
        if (typeof item === "object") {
          return item?._id || item?.id || item?.title || item?.name || String(item);
        }
        return String(item);
      })
      .filter(Boolean)
      .sort()
      .join(",");
  }

  if (typeof value === "object") {
    return value?._id || value?.id || JSON.stringify(value);
  }

  return String(value);
};

export const isLiveOfferStatus = (status) => LIVE_OFFER_STATUSES.has(String(status || "").toLowerCase());

export const shouldPreserveLiveOffer = ({ currentPayload, originalOffer, isLive }) => {
  if (!isLive || !currentPayload || !originalOffer) return false;

  const canonicalCurrent = getCanonicalLiveOfferPayload(currentPayload);
  const canonicalOriginal = getCanonicalLiveOfferPayload(originalOffer);

  const changedKeys = [...LIVE_OFFER_COMPARISON_FIELDS].filter((key) => {
    if (shouldIgnoreField(key, currentPayload, originalOffer)) {
      return false;
    }

    const currentValue = normalizeOfferValue(key, canonicalCurrent?.[key]);
    const originalValue = normalizeOfferValue(key, canonicalOriginal?.[key]);
    return currentValue !== originalValue;
  });

  return changedKeys.length === 0 || changedKeys.every((key) => APPROVAL_SAFE_EDIT_FIELDS.has(key));
};

export const getLiveOfferSaveFlags = ({
  currentPayload,
  originalOffer,
  isLive,
  fallbackIsCreated = false,
  fallbackStatus = null,
}) => {
  const preserveLiveOffer = shouldPreserveLiveOffer({
    currentPayload,
    originalOffer,
    isLive,
  });

  return {
    preserveLiveOffer,
    isCreated: preserveLiveOffer ? true : fallbackIsCreated,
    status: preserveLiveOffer ? "live" : fallbackStatus,
  };
};
